import React from 'react';

import Chip from '~/components/core/Atomic/Chip/Chip';
import Text from '~/components/core/TextComponents/Text';
import PreviewCard from '~/components/ui/atoms/PreviewCard';

/** The look every reference chip shares, so the expert chip and these cannot drift apart. */
export const REFERENCE_CHIP_CLASS_NAME =
  'h-20 max-w-[240px] bg-azure-200 text-azure-700 hover:bg-azure-200 hover:text-azure-700';
export const REFERENCE_CHIP_LABEL_CLASS_NAME = 'block max-w-[240px] truncate text-xs font-semibold';

/**
 * PlainReferenceChip
 *
 * A cited source with nothing to open: a chip with its label, and on hover a card with its title, an optional
 * secondary line (such as a date) and its body text. `asText` keeps the sentence's own words instead of a chip.
 */

interface PlainReferenceChipProps {
  label: React.ReactNode;
  title?: React.ReactNode;
  secondary?: string;
  body?: string | null;
  asText?: boolean;
}

export const PlainReferenceChip: React.FC<PlainReferenceChipProps> = ({ label, title, secondary, body, asText }) => {
  const trigger = asText ? (
    <span className="cursor-default underline decoration-dotted underline-offset-2">{label}</span>
  ) : (
    <Chip
      label={<span className={REFERENCE_CHIP_LABEL_CLASS_NAME}>{label}</span>}
      size="small"
      className={REFERENCE_CHIP_CLASS_NAME}
    />
  );

  return (
    <PreviewCard trigger={trigger}>
      <div className="flex max-w-[400px] flex-col items-start gap-4 rounded-lg p-12">
        {title ? (
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD} className="w-full truncate">
            {title}
          </Text>
        ) : null}
        {secondary ? (
          <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.REGULAR} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
            {secondary}
          </Text>
        ) : null}
        {body ? (
          <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.REGULAR} className="whitespace-pre-line">
            {body}
          </Text>
        ) : null}
      </div>
    </PreviewCard>
  );
};
